import { useMemo } from "react";
import { useDebounce } from "@reactuses/core";
import { useServerTable } from "../../../components/table/tableFeatures";
import { MdRotateLeft } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { RT8ColumnSelector } from "../../../components/core/blocks/MetricSelector";
import { SearchInput } from "../../../components/fields/base/TextField";
import { RT8Pagination } from "../../../components/table/Pagination";
import { Table } from "../../../components/table/Table";
import { createColumnBuilder } from "../../../components/table/columnBuilder";
import { resetTable } from "../../../components/table/utils";
import { useBlockWindowQuery } from "../../../components/table/useBlockWindowQuery";
import { usePersistedTableState } from "../../../components/table/usePersistedTableState";
import { useStandardEntityMetrics } from "../../merchandising/components/tables/metricUtils";
import { Button } from "../../../components/core/basic/Button";
import { RT8FilterWidget } from "../../merchandising/components/tables/FilterWidget";
import { useGetDSPGeoPaginated } from "../hooks/useDSPGeoPaginatedQuery";
import type { DSPGeoRow } from "../../../zodios/apiDefinitions/api/dsp/geoDatasets";

const columnHelper = createColumnBuilder<DSPGeoRow>();

type Props = {
  lineItemId?: string | number | null;
  orderId?: string | number | null;
  showAll?: boolean;
};

export const DSPGeoTable = ({ lineItemId, orderId, showAll }: Props) => {
  const { t } = useTranslation(["dsp"]);
  const standardMetricIds = useStandardEntityMetrics("dspGeo");

  const { state, initialState, setters } = usePersistedTableState({
    persistKey: "dspGeoTable",
    initialState: {
      sorting: [{ id: "dsp_total_detail_page_views", desc: true }],
    },
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row.region || t(($) => $.dsp["Other"]), {
        id: "region",
        header: t(($) => $.dsp["Region"]),
        minSize: 200,
        size: 300,
        maxSize: 500,
        enableColumnFilter: false,
        meta: {
          columnSelectorOptions: { label: t(($) => $.dsp["Region"]) },
        },
      }),
      columnHelper.accessor("country", {
        id: "country",
        header: t(($) => $.dsp["Country"]),
        size: 170,
        meta: {
          columnSelectorOptions: { label: t(($) => $.dsp["Country"]) },
        },
      }),
      ...standardMetricIds.map((id) => columnHelper.metric(id)),
    ],
    [standardMetricIds, t],
  );

  const debouncedSearch = useDebounce(state.globalFilter ?? "", 500);

  const getQueryOptions = useGetDSPGeoPaginated({ lineItemId, orderId, showAll });
  const geoQuery = useBlockWindowQuery(
    getQueryOptions,
    {
      pagination: state.pagination,
      sorting: state.sorting,
      filters: state.columnFilters,
      search: debouncedSearch,
    },
    { enabled: !!state },
  );

  const tableData = useMemo(() => geoQuery.data?.data ?? [], [geoQuery.data]);

  const table = useServerTable({
    data: tableData,
    columns,
    initialState,
    state,
    ...setters,
    rowCount: geoQuery.data?.count ?? undefined,
    meta: {
      showSkeleton: geoQuery.isFetching && (!geoQuery.data || geoQuery.isPlaceholderData),
    },
  });

  return (
    <>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              onPress={() => {
                resetTable(table);
              }}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={t(($) => $.dsp["Search"])}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={table.state.globalFilter}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table table={table} className={css({ minWidth: "100%" })} />
    </>
  );
};
